import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MessageSquare, Calendar, Send, LogOut } from "lucide-react";
import { getPrefs, setPrefs, sendTestReminder } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const FREQUENCIES = ["daily", "weekly", "monthly"];
const TOPICS = [
  { id: "diet", label: "Diet tips" },
  { id: "labs", label: "Lab reminders" },
  { id: "herbal", label: "Herbal suggestions" },
];
const CHANNELS = [
  { id: "email", label: "Email", Icon: Mail },
  { id: "sms", label: "SMS (coming soon)", Icon: MessageSquare, disabled: true },
];

export default function AccountPage() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [prefs, setLocalPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    if (!user.is_subscribed) { setLocalPrefs(null); return; }
    getPrefs().then(setLocalPrefs).catch(() => {});
  }, [user]);

  const update = (patch) => setLocalPrefs(p => ({ ...p, ...patch }));
  const toggleTopic = (t) => {
    const list = prefs.topics.includes(t) ? prefs.topics.filter(x => x !== t) : [...prefs.topics, t];
    update({ topics: list });
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await setPrefs(prefs);
      setLocalPrefs(updated);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    try {
      const r = await sendTestReminder();
      if (r.sent) toast.success("Test reminder sent to your email");
      else toast.message("Email backend not yet configured — preview saved.", { description: "Add RESEND_API_KEY in backend/.env to enable sending." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    }
  };

  if (loading || !user) return null;

  return (
    <div className="relative z-10 min-h-screen" data-testid="account-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 flex items-center justify-between">
        <Link to="/" className="font-serif-display text-2xl">SafeRxCheck</Link>
        <button onClick={signOut} className="inline-flex items-center gap-2 label-small h-11 px-2" data-testid="logout-btn">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </header>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-12 pb-8 sm:pb-10">
        <p className="label-small">YOUR ACCOUNT</p>
        <h1 className="font-serif-display text-[2.5rem] leading-[1.05] sm:text-5xl mt-2" style={{ color: "var(--text-primary)" }}>
          {user.name?.split(" ")[0] || "Hello"}.
        </h1>
        <p className="mt-3 text-sm sm:text-base break-all" style={{ color: "var(--text-secondary)" }} data-testid="account-email">{user.email}</p>

        <div className="mt-8 rounded-2xl border p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4"
             style={{ background: "white", borderColor: "var(--border-default)" }}
             data-testid="subscription-status">
          <div>
            <p className="label-small">Subscription</p>
            <p className="font-serif-display text-2xl mt-1" style={{ color: user.is_subscribed ? "var(--brand)" : "var(--terracotta)" }}>
              {user.is_subscribed ? "Active" : "Inactive"}
            </p>
            {user.subscription_until && (
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                Until {new Date(user.subscription_until).toLocaleString()}
              </p>
            )}
          </div>
          {!user.is_subscribed && (
            <Link to="/pricing" className="w-full sm:w-auto inline-flex items-center justify-center rounded-full px-6 h-12 text-white" style={{ background: "var(--brand)" }} data-testid="upgrade-btn">
              Subscribe →
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="rounded-2xl border p-6 sm:p-8 lg:p-10" style={{ background: "white", borderColor: "var(--border-default)" }}>
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="h-5 w-5" style={{ color: "var(--brand)" }} strokeWidth={1.5} />
            <h2 className="font-serif-display text-2xl" style={{ color: "var(--text-primary)" }}>Reminders</h2>
          </div>

          {!user.is_subscribed && (
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Reminders unlock with a subscription. <Link to="/pricing" className="underline">Start your 5-day trial →</Link>
            </p>
          )}

          {user.is_subscribed && prefs && (
            <div className="space-y-8">
              <div>
                <p className="label-small mb-3">Frequency</p>
                <div className="flex flex-wrap gap-2">
                  {FREQUENCIES.map(f => (
                    <button key={f} onClick={() => update({ frequency: f })}
                            className={`rounded-full px-5 h-11 text-sm border transition-all ${prefs.frequency === f ? "text-white" : ""}`}
                            style={{
                              background: prefs.frequency === f ? "var(--brand)" : "transparent",
                              borderColor: prefs.frequency === f ? "var(--brand)" : "var(--border-default)",
                              color: prefs.frequency === f ? "white" : "var(--text-secondary)",
                            }}
                            data-testid={`freq-${f}`}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="label-small mb-3">Topics</p>
                <div className="flex flex-wrap gap-2">
                  {TOPICS.map(t => {
                    const active = prefs.topics.includes(t.id);
                    return (
                      <button key={t.id} onClick={() => toggleTopic(t.id)}
                              className="rounded-full px-5 h-11 text-sm border transition-all"
                              style={{
                                background: active ? "var(--brand-light)" : "transparent",
                                borderColor: active ? "var(--brand)" : "var(--border-default)",
                                color: active ? "var(--brand)" : "var(--text-secondary)",
                              }}
                              data-testid={`topic-${t.id}`}>
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="label-small mb-3">Channels</p>
                <div className="space-y-2">
                  {CHANNELS.map(({ id, label, Icon, disabled }) => {
                    const active = prefs.channels.includes(id);
                    return (
                      <label key={id}
                             className="flex items-center gap-3 rounded-xl border p-4 cursor-pointer"
                             style={{ borderColor: "var(--border-default)", opacity: disabled ? 0.5 : 1 }}>
                        <input type="checkbox" checked={active} disabled={disabled}
                               onChange={(e) => {
                                 const list = e.target.checked
                                   ? [...new Set([...prefs.channels, id])]
                                   : prefs.channels.filter(c => c !== id);
                                 update({ channels: list });
                               }}
                               data-testid={`channel-${id}`} />
                        <Icon className="h-4 w-4" style={{ color: "var(--brand)" }} />
                        <span className="text-sm">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3 pt-2">
                <button onClick={save} disabled={saving}
                        className="w-full sm:w-auto rounded-full px-6 h-12 text-white disabled:opacity-50"
                        style={{ background: "var(--brand)" }}
                        data-testid="save-prefs-btn">
                  {saving ? "Saving…" : "Save preferences"}
                </button>
                <button onClick={test}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-6 h-12 border"
                        style={{ borderColor: "var(--brand)", color: "var(--brand)" }}
                        data-testid="send-test-btn">
                  <Send className="h-4 w-4" /> Send test reminder
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
